import { classNames } from '@framework/utils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { usePopupRef } from '@framework/hooks';
import { faAngleRight } from '@fortawesome/free-solid-svg-icons';
import { LinkColor } from '@model-framework/link';
import { forwardRef, useCallback, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
type Props = {
  onChangeColor(color: LinkColor): void;
  onOpenSubMenu(subMenu: string): void;
};

export const LinkColorMenuButton = forwardRef<
  {
    closePopup: () => void;
  },
  Props
>(({ onChangeColor, onOpenSubMenu }, ref) => {
  const { t } = useTranslation();
  const { isOpen, popupRef, handleToggle, handleClose } = usePopupRef<HTMLDivElement>(false);

  useImperativeHandle(ref, () => ({
    closePopup: () => handleClose(),
  }));

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      e.stopPropagation();
      onOpenSubMenu('linkColor');
      handleToggle();
    },
    [handleToggle, onOpenSubMenu]
  );

  return (
    <div className="relative hover:bg-gray-200">
      <button
        // data-right-click-menu は親 item をクリックしても閉じないようにするためのマーカー
        data-right-click-menu
        className={classNames('flex w-full cursor-pointer px-8 py-4')}
        onClick={handleClick}
      >
        <div className="inline-block pl-4 pr-6">{t('canvas.menu.color')}</div>
        <FontAwesomeIcon icon={faAngleRight} className="ml-auto" />
      </button>
      <div
        className={classNames(
          'absolute -right-72 top-0 z-50 w-72 rounded-lg bg-white py-4 shadow-2xl',
          isOpen ? 'block' : 'hidden'
        )}
        ref={popupRef}
      >
        <ColorButton onSelectColor={onChangeColor} color={LinkColor.Red} />
        <ColorButton onSelectColor={onChangeColor} color={LinkColor.Blue} />
        <ColorButton onSelectColor={onChangeColor} color={LinkColor.Gray} />
      </div>
    </div>
  );
});
LinkColorMenuButton.displayName = 'LinkColorMenuButton';

type ColorButtonProps = {
  onSelectColor(color: LinkColor): void;
  color: LinkColor;
};

const ColorButton: React.FC<ColorButtonProps> = ({ onSelectColor, color }: ColorButtonProps) => {
  const { t } = useTranslation();
  const handleClick = useCallback(() => {
    onSelectColor(color);
  }, [onSelectColor, color]);

  return (
    <button className="flex w-full py-6 pl-8 pr-12 hover:bg-gray-300" onClick={handleClick}>
      <span className="mr-8 size-16 flex-none" style={{ backgroundColor: LinkColor.colorCodeOf(color) }} />
      <span className="flex-none">{t(LinkColor.toColorNameKey(color))}</span>
    </button>
  );
};
